import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, createFilterOptions,
} from '@mui/material';
import type { CustomerSummary } from '../../api/types';
import { useCustomerContacts, useCustomers, useSaveCustomer } from '../../api/hooks';
import { useErrorMessage } from '../common/Common';
import { OpportunityFormDialog } from './OpportunityFormDialog';

/** An entry the user can pick to create the customer they just typed, rather than abandoning the
 *  opportunity to go and create it first. */
interface CreateOption {
  create: true;
  name: string;
}

type Option = CustomerSummary | CreateOption;

const isCreate = (option: Option): option is CreateOption => 'create' in option;

const filter = createFilterOptions<Option>();

/** From the board there is no customer in context yet, so pick one first, then open the normal
 *  opportunity form. Keeps the board's "+ New opportunity" honest without duplicating the form. */
export function NewOpportunityFlow({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const describeError = useErrorMessage();

  const [customerId, setCustomerId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const { data: customers } = useCustomers();
  const { data: contacts } = useCustomerContacts(customerId ?? 0);
  const saveCustomer = useSaveCustomer();

  // Held separately so a customer created here is selectable immediately, without waiting for the
  // list query to come back around.
  const [created, setCreated] = useState<CustomerSummary | null>(null);

  const options: Option[] = customers?.items ?? [];
  const selected = created ?? options.find((o) => !isCreate(o) && o.id === customerId) ?? null;

  const closeAll = () => {
    setFormOpen(false);
    setCustomerId(null);
    setCreated(null);
    saveCustomer.reset();
    onClose();
  };

  const choose = async (option: Option | null) => {
    if (option === null) {
      setCustomerId(null);
      setCreated(null);
      return;
    }

    if (!isCreate(option)) {
      setCreated(null);
      setCustomerId(option.id);
      return;
    }

    // Only a name is captured here: the point is to keep the user in the flow they started.
    // Everything else can be filled in on the customer page later.
    const saved = await saveCustomer.mutateAsync({
      body: { name: option.name, website: null, country: null, notes: null, partnerId: null },
    });
    setCreated({
      id: saved.id,
      name: saved.name,
      country: saved.country,
      emailDomain: saved.emailDomain,
      partnerId: saved.partnerId,
      partnerName: saved.partnerName,
      ownerUserId: saved.ownerUserId,
      ownerName: saved.ownerName,
      openOpportunityCount: 0,
      updatedAtUtc: saved.updatedAtUtc,
    });
    setCustomerId(saved.id);
  };

  return (
    <>
      <Dialog open={open && !formOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>{t('forms.newOpportunityFlow.title')}</DialogTitle>
        <DialogContent>
          {saveCustomer.error && (
            <Alert severity="error" sx={{ mt: 1 }}>{describeError(saveCustomer.error)}</Alert>
          )}
          <Autocomplete
            sx={{ pt: 1 }}
            options={options}
            value={selected}
            onChange={(_, value) => void choose(value)}
            getOptionLabel={(option) =>
              isCreate(option) ? option.name : option.name}
            isOptionEqualToValue={(a, b) => !isCreate(a) && !isCreate(b) && a.id === b.id}
            filterOptions={(opts, params) => {
              const filtered = filter(opts, params);
              const typed = params.inputValue.trim();
              const exists = opts.some(
                (o) => !isCreate(o) && o.name.toLowerCase() === typed.toLowerCase());
              if (typed.length > 0 && !exists) filtered.push({ create: true, name: typed });
              return filtered;
            }}
            renderOption={(props, option) => (
              <li {...props} key={isCreate(option) ? '__create' : option.id}>
                {isCreate(option)
                  ? t('forms.newOpportunityFlow.createCustomer', { name: option.name })
                  : option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('forms.newOpportunityFlow.customer')}
                placeholder={t('common.startTyping')}
                helperText={t('forms.newOpportunityFlow.createHint')}
                slotProps={{ htmlInput: { ...params.inputProps, 'data-testid': 'new-opp-customer-input' } }}
              />
            )}
            data-testid="new-opp-customer"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeAll} disabled={saveCustomer.isPending}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!customerId || saveCustomer.isPending}
            onClick={() => setFormOpen(true)}
            data-testid="new-opp-continue"
          >
            {t('common.continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {customerId && (
        <OpportunityFormDialog
          open={formOpen}
          customerId={customerId}
          contacts={contacts ?? []}
          onClose={closeAll}
          onSaved={(opp) => navigate(`/opportunities/${opp.id}`)}
        />
      )}
    </>
  );
}
